import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { authApi } from '../services/api';
import { extractApiError } from '../utils/errorUtils'; // Issue #964: Standardized error handling

export default function EmailVerification() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link. No token provided.');
        return;
      }

      try {
        await authApi.verifyEmail(token);
        setStatus('success');
        setMessage('Your email has been verified successfully!');

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } catch (err: unknown) {
        setStatus('error');
        const errorMessage = extractApiError(err).message;
        setMessage(errorMessage || 'Verification failed. The link may be invalid or expired.');
      }
    };

    verifyEmail();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-teal-50">
      {/* Header matches standard design (Issue #1509) */}
      <nav className="bg-primary text-white shadow-lg">
        <div className="w-full pl-0 pr-3 py-0.5">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              {/* Logo matches main Navbar - 20% larger with 25% less padding (Issue #1509) */}
              <picture>
                <source srcSet="/logo.webp" type="image/webp" />
                <img src="/logo.png" alt="Local Artist Fairs" className="h-24 w-auto" />
              </picture>
            </Link>
            <div className="flex space-x-1.5">
              <Link to="/" className="hover:text-secondary-light transition-colors text-sm md:text-base">Back to Site</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Verification Status Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="text-center">
              {status === 'verifying' && (
                <>
                  <Loader2 className="h-16 w-16 text-secondary mx-auto mb-4 animate-spin" />
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Verifying Email</h2>
                  <p className="text-gray-600">Please wait while we verify your email address...</p>
                </>
              )}

              {status === 'success' && (
                <>
                  <CheckCircle className="h-16 w-16 text-secondary mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Email Verified!</h2>
                  <p className="text-gray-600 mb-6">{message}</p>
                  <p className="text-sm text-gray-500">Redirecting to login page in 3 seconds...</p>
                  <div className="mt-6">
                    <Link
                      to="/login"
                      className="inline-block bg-primary text-white px-6 py-2 rounded-md hover:bg-primary-dark transition"
                    >
                      Go to Login Now
                    </Link>
                  </div>
                </>
              )}

              {status === 'error' && (
                <>
                  <XCircle className="h-16 w-16 text-red-600 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Verification Failed</h2>
                  <p className="text-gray-600 mb-6">{message}</p>
                  <div className="space-y-3">
                    <Link
                      to="/login"
                      className="block w-full bg-primary text-white px-6 py-2 rounded-md hover:bg-primary-dark transition text-center"
                    >
                      Go to Login
                    </Link>
                    <Link
                      to="/signup"
                      className="block w-full bg-white text-secondary border border-primary px-6 py-2 rounded-md hover:bg-secondary-light/50 transition text-center"
                    >
                      Register New Account
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
